import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  CarrierSchema,
  ServiceSchema,
} from "../fulfillments/fulfillment-carriers-and-services";
import {
  findShipmentByTracker,
  FulfillmentGroup,
  FulfillmentGroupData,
} from "../fulfillments/fulfillment-group";
import {
  OmsInsuranceProvider,
  ShipmentTrackerStatus,
} from "../fulfillments/outbound-shipment-label";
import { Provider } from "../order";
import { RateProvider } from "../outbound-labels/outbound-labels";
import { ShippingProviderType } from "../shipment";
import { FedexClaimInfoSchema } from "./carrier-claim-info/fedex";
import { UpsClaimInfoSchema } from "./carrier-claim-info/ups";
import { ShippingClaimEventSchema } from "./shipping-claim-events";

export const DEFAULT_REDO_KEEP_PROPORTION = 0.25;
export const APPROXIMATE_APPROVAL_RATE = 0.68;

export enum ShippingClaimReason {
  LATE = "late",
  DAMAGED = "damaged",
  LOST = "lost",
  STOLEN = "stolen",
}

export enum ShippingClaimType {
  OUTBOUND = "outbound",
  RETURN = "return",
}

export enum ShippingClaimStatus {
  REQUESTED = "requested", // Merchant requested a claim
  SUBMITTED = "submitted", // Redo submitted a claim to the carrier
  CARRIER_UNSUBMITTED_MERCHANT_APPROVED = "carrier_unsubmitted_merchant_approved", // Redo has not submitted the claim to the carrier, but we approved the merchant's claim
  CARRIER_SUBMITTED_MERCHANT_APPROVED = "carrier_submitted_merchant_approved", // Redo submitted the claim to the carrier, and we approved the merchant's claim
  APPROVED = "approved", // Carrier approved the claim
  REJECTED = "rejected", // Carrier rejected the claim
  PENDING_DOCS = "pending_docs", // Carrier needs more docs
}

export const NonTerminalClaimStatuses = [
  ShippingClaimStatus.REQUESTED,
  ShippingClaimStatus.SUBMITTED,
  ShippingClaimStatus.PENDING_DOCS,
  ShippingClaimStatus.CARRIER_UNSUBMITTED_MERCHANT_APPROVED,
  ShippingClaimStatus.CARRIER_SUBMITTED_MERCHANT_APPROVED,
];

// Status displayed in the admin dashboard
export const ShippingClaimStatusToInternalLabel: Record<
  ShippingClaimStatus,
  string
> = {
  [ShippingClaimStatus.REQUESTED]: "Requested",
  [ShippingClaimStatus.SUBMITTED]: "Submitted to carrier",
  [ShippingClaimStatus.CARRIER_UNSUBMITTED_MERCHANT_APPROVED]:
    "Approved merchant claim, still needs carrier claim",
  [ShippingClaimStatus.CARRIER_SUBMITTED_MERCHANT_APPROVED]:
    "Approved merchant claim, submitted claim to carrier",
  [ShippingClaimStatus.APPROVED]: "Approved",
  [ShippingClaimStatus.REJECTED]: "Rejected",
  [ShippingClaimStatus.PENDING_DOCS]: "Pending docs",
};

// Status displayed to the merchant
export const ShippingClaimStatusToMerchantFacingLabel: Record<
  ShippingClaimStatus,
  string
> = {
  [ShippingClaimStatus.REQUESTED]: "Requested",
  [ShippingClaimStatus.SUBMITTED]: "Submitted to carrier",
  [ShippingClaimStatus.PENDING_DOCS]: "Pending documentation",
  [ShippingClaimStatus.CARRIER_UNSUBMITTED_MERCHANT_APPROVED]: "Approved",
  [ShippingClaimStatus.CARRIER_SUBMITTED_MERCHANT_APPROVED]: "Approved",
  [ShippingClaimStatus.APPROVED]: "Approved",
  [ShippingClaimStatus.REJECTED]: "Rejected",
};

// We want merchants to see that we are on top of claims - so after 6 hours, we show "Submitted to carrier"
// We don't actually want to change the status in the backend because we want the admin dash to be clear about the status
export function getMerchantFacingStatus(
  claim: Pick<ShippingClaim, "status" | "submittedAt">,
): ShippingClaimStatus {
  if (claim.status === ShippingClaimStatus.REQUESTED) {
    const sixHoursAgo = new Date(Date.now() - 6 * 60 * 60 * 1000);
    if (claim.submittedAt < sixHoursAgo) {
      return ShippingClaimStatus.SUBMITTED;
    }
  }

  return claim.status;
}

export enum ShippingClaimSource {
  MERCHANT = "merchant",
  REDO = "redo",
}

export enum ShippingClaimRequestedBy {
  MERCHANT = "merchant",
  PROACTIVE = "proactive",
}

export enum ShippingClaimDenialReason {
  PACKAGE_DELIVERED = "package_delivered",
  INSUFFICIENT_DOCUMENTATION = "insufficient_documentation",
  OUTSIDE_FILING_WINDOW = "outside_filing_window",
  VALUE_NOT_SUBSTANTIATED = "value_not_substantiated",
  PROOF_OF_DELIVERY_MISSING = "proof_of_delivery_missing",
  NOT_COVERED_BY_SERVICE_LEVEL = "not_covered_by_service_level",
  DUPLICATE_CLAIM = "duplicate_claim",
  CLAIM_ALREADY_PAID = "claim_already_paid",
  OTHER = "other",
}

export const ShippingClaimDenialReasonToLabel: Record<
  ShippingClaimDenialReason,
  string
> = {
  [ShippingClaimDenialReason.PACKAGE_DELIVERED]: "Package delivered",
  [ShippingClaimDenialReason.INSUFFICIENT_DOCUMENTATION]:
    "Insufficient documentation",
  [ShippingClaimDenialReason.OUTSIDE_FILING_WINDOW]: "Outside filing window",
  [ShippingClaimDenialReason.VALUE_NOT_SUBSTANTIATED]:
    "Value not substantiated",
  [ShippingClaimDenialReason.PROOF_OF_DELIVERY_MISSING]:
    "Proof of delivery missing",
  [ShippingClaimDenialReason.NOT_COVERED_BY_SERVICE_LEVEL]:
    "Not covered by service level",
  [ShippingClaimDenialReason.DUPLICATE_CLAIM]: "Duplicate claim",
  [ShippingClaimDenialReason.CLAIM_ALREADY_PAID]: "Claim already paid",
  [ShippingClaimDenialReason.OTHER]: "Other",
};

export const ShippingClaimPhotoSchema = z.object({
  url: z.string(),
  contentType: z.string(),
  fileName: z.string(),
  uploadedAt: z.date(),
  description: z.string().nullish(),
});

export type ShippingClaimPhoto = z.infer<typeof ShippingClaimPhotoSchema>;

export const CarrierSpecificClaimInfoSchema = z.object({
  ups: UpsClaimInfoSchema.nullish(),
  fedex: FedexClaimInfoSchema.nullish(),
});
export type CarrierSpecificClaimInfo = z.infer<
  typeof CarrierSpecificClaimInfoSchema
>;

const BaseShippingClaimSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  teamName: z.string(),
  orderName: z.string(),
  customerName: z.string().nullish(),
  customerId: z.string().nullish(),
  type: createEnum(ShippingClaimType, { enumName: "ShippingClaimType" }),
  reason: createEnum(ShippingClaimReason, { enumName: "ShippingClaimReason" }),
  status: createEnum(ShippingClaimStatus, { enumName: "ShippingClaimStatus" }),
  source: createEnum(ShippingClaimSource, { enumName: "ShippingClaimSource" }),
  requestedBy: createEnum(ShippingClaimRequestedBy, {
    enumName: "ShippingClaimRequestedBy",
  }).nullish(),
  denialReason: createEnum(ShippingClaimDenialReason, {
    enumName: "ShippingClaimDenialReason",
  }).nullish(),
  submittedAt: z.date(),
  trackingCode: z.string(),
  publicTrackingUrl: z.string(),
  shipmentId: z.string(),
  carrier: CarrierSchema,
  service: ServiceSchema,
  rateProvider: z
    .union([
      createEnum(RateProvider, { enumName: "RateProvider" }),
      createEnum(ShippingProviderType, { enumName: "ShippingProviderType" }),
    ])
    .nullish(),
  upsGhostAccountId: z.string().nullish(),
  firstScanAt: z.date().nullish(),
  orderProvider: createEnum(Provider, { enumName: "Provider" }).nullish(),
  externalOrderLink: z.string().nullish(),
  labelCostToRedo: z.string(),
  labelCostToMerchant: z.string(),
  goodsValue: z.string(),
  insuredValue: z.string().nullish(),
  insuranceProvider: createEnum(OmsInsuranceProvider, {
    enumName: "OmsInsuranceProvider",
  }).nullish(),
  carrierClaimNumber: z.string().nullish(),
  carrierClaimSubmittedAt: z.date().nullish(),
  refundIssued: z.boolean(),
  refundId: z.string().nullish(),
  refundedAt: z.date().nullish(),
  merchantRefundAmount: z.string().nullish(),
  carrierRefundAmount: z.string().nullish(),
  internalNote: z.string(),
  merchantInternalNotes: z.string().nullish(),
  photos: z.array(ShippingClaimPhotoSchema).nullish(),
  notes: z.string().nullish(),
  notifiedMerchantOnStatus: z.boolean().nullish(),
  internalOrderLink: z.string().nullish(),
  createdAt: z.date(),
  updatedAt: z.date(),
  shipsuranceClaimId: z.string().nullish(),
  shipsuranceClaimRetrievalCode: z.string().nullish(),
  shipsuranceMessage: z.string().nullish(),
  events: z.array(ShippingClaimEventSchema).nullish(),
  claimInvoiceUrl: z.string().nullish(),
  automationAttempts: z.number().default(0).optional(),
  lastStatusCheckedAt: z.date().nullish(),
  // Count of status checks that found no carrier record; once it crosses the
  // threshold the claim is marked statusCheckAbandoned and dropped from checks.
  statusCheckNotFoundCount: z.number().nullish(),
  statusCheckAbandoned: z.boolean().nullish(),
  redoKeepRevenue: z.number().nullish(),
  carrierSpecificInfo: CarrierSpecificClaimInfoSchema.nullish(),
});

export type BaseShippingClaim = z.infer<typeof BaseShippingClaimSchema>;

export const OMSShippingClaimSchema = BaseShippingClaimSchema.extend({
  type: z.literal(ShippingClaimType.OUTBOUND),
  fulfillmentGroupId: zExt.objectId(),
});

// For whatever other kinds of claims we need to support next (returns I think)
const ReturnShippingClaimSchema = BaseShippingClaimSchema.extend({
  type: z.literal(ShippingClaimType.RETURN),
  returnId: zExt.objectId(),
});

export const ShippingClaimSchema = z.union([
  OMSShippingClaimSchema,
  ReturnShippingClaimSchema,
]);

export type ShippingClaim = z.infer<typeof ShippingClaimSchema>;

export const CreateOmsShippingClaimSchema = OMSShippingClaimSchema.omit({
  _id: true,
  createdAt: true,
  updatedAt: true,
});

export type CreateOmsShippingClaim = z.infer<
  typeof CreateOmsShippingClaimSchema
>;

export const CreateReturnShippingClaimSchema = ReturnShippingClaimSchema.omit({
  _id: true,
  createdAt: true,
  updatedAt: true,
});

export type CreateReturnShippingClaim = z.infer<
  typeof CreateReturnShippingClaimSchema
>;

export const ShippingClaimSubmissionSchema = z.object({
  fulfillmentGroupId: zExt.objectId(),
  reason: createEnum(ShippingClaimReason, { enumName: "ShippingClaimReason" }),
  photos: z.array(ShippingClaimPhotoSchema).optional(),
  additionalDetails: z.string().optional(),
});

export type ShippingClaimSubmission = z.infer<
  typeof ShippingClaimSubmissionSchema
>;

export const shippingClaimUpdateSchema = z.object({
  shippingClaimId: z.string(),
  status: createEnum(ShippingClaimStatus, {
    enumName: "ShippingClaimStatus",
  }).nullish(),
  carrierRefundAmount: z.string().nullish(),
  carrierClaimNumber: z.string().nullish(),
  internalNote: z.string().nullish(),
  merchantInternalNotes: z.string().nullish(),
  carrierClaimSubmittedAt: z.date().nullish(),
  refundIssued: z.boolean().nullish(),
  source: createEnum(ShippingClaimSource, {
    enumName: "ShippingClaimSource",
  }).nullish(),
  requestedBy: createEnum(ShippingClaimRequestedBy, {
    enumName: "ShippingClaimRequestedBy",
  }).nullish(),
  denialReason: createEnum(ShippingClaimDenialReason, {
    enumName: "ShippingClaimDenialReason",
  }).nullish(),
  shipsuranceMessage: z.string().nullish(),
});

export type ShippingClaimUpdate = z.infer<typeof shippingClaimUpdateSchema>;

export const CARRIER_INSURANCE_THRESHOLD = 100;

export function ensureCanCreateClaim(
  fulfillmentGroupData: FulfillmentGroupData | FulfillmentGroup,
  trackingNumber: string,
) {
  if (!fulfillmentGroupData.shipment) {
    return false;
  }

  const relevantShipment = findShipmentByTracker(
    fulfillmentGroupData,
    trackingNumber,
  );

  const boughtRate = relevantShipment?.boughtRate;

  const alreadySubmitted = fulfillmentGroupData.shippingClaimIdsByTrackingNumber
    ? (fulfillmentGroupData.shippingClaimIdsByTrackingNumber[trackingNumber]
        ?.length ?? 0) > 0
    : (fulfillmentGroupData.shippingClaimIds?.length ?? 0) > 0;

  // If a merchant bought additional insurance above the threshold automatically included by carriers, we need to let them submit a claim - we will submit this to shipsurance/easypost
  const hasExtraInsurance =
    relevantShipment &&
    relevantShipment.parcel?.insurance?.insuredAmount &&
    relevantShipment.parcel.insurance.insuredAmount >
      CARRIER_INSURANCE_THRESHOLD;

  // Only allow claim submission for redo carrier accounts - do not allow for merchant carrier accounts unless they purchased extra insurance
  if (
    alreadySubmitted ||
    !boughtRate ||
    (boughtRate.is_merchant_carrier_account && !hasExtraInsurance)
  ) {
    return false;
  }

  return boughtRate;
}

export function getShippingClaimInternalLink(
  claim: ShippingClaim,
): string | undefined {
  if (claim.internalOrderLink) {
    return claim.internalOrderLink;
  }
  if (claim.type === ShippingClaimType.RETURN) {
    return `/order-management/returns/all-returns/${claim.returnId}`;
  }
  return undefined;
}

export function groupHasClaimableShipment(
  fulfillmentGroupData: FulfillmentGroupData | FulfillmentGroup,
) {
  return [
    fulfillmentGroupData.shipment,
    ...(fulfillmentGroupData.additionalShipments ?? []),
  ].some((shipment) =>
    ensureCanCreateClaim(
      fulfillmentGroupData,
      shipment?.tracker?.trackingCode ?? "",
    ),
  );
}

export function canOnlySubmitThroughRedo(
  rateProvider: ShippingClaim["rateProvider"],
): boolean {
  return rateProvider === RateProvider.UPS;
}

export function findEarliestOmsInTransitDate(
  trackingDetails?:
    | { datetime: Date; status: ShipmentTrackerStatus }[]
    | null
    | undefined,
): Date | null {
  if (!trackingDetails) {
    return null;
  }

  const inTransitEvents = trackingDetails
    .filter((detail) => detail.status === ShipmentTrackerStatus.IN_TRANSIT)
    .sort((a, b) => a.datetime.getTime() - b.datetime.getTime());

  return inTransitEvents.length > 0 && inTransitEvents[0]?.datetime
    ? inTransitEvents[0].datetime
    : null;
}
